import { get } from "svelte/store";
import cfg from "$lib/config/config";
import { locale } from "svelte-i18n";
import type { Page } from "@sveltejs/kit";
import { browser } from "$app/environment";

// allowedParams is a preset list of known query params that are included in URL metadata
const allowedParams = ["lang", "colour", "size", "gender", "type", "unit"];

export const queryToString = (q: any): string => {
  const query = Object.entries(q)
    .filter(obj => allowedParams.includes(obj[0]))
    .map(([key, v]) => {
      if (!v) {
        return "";
      }
      return (
        key + "=" + encodeURIComponent(typeof v === "string" ? v : v.join(","))
      );
    })
    .filter(v => v !== "")
    .join("&");
  return query ? "?" + query : "";
};

export function stripBasePath(url: string): string {
  return url.startsWith(cfg.BASE_PATH)
    ? url.substring(cfg.BASE_PATH.length, url.length)
    : url;
}

export function stripBasePathAndLang(url: string): string {
  let strippedUrl = stripBasePath(url);
  const langPattern = /^\/[a-z]{2}(\/|$)/;
  if (langPattern.test(strippedUrl)) {
    strippedUrl = strippedUrl.replace(langPattern, "");
  }
  if (strippedUrl.startsWith("/")) {
    strippedUrl = strippedUrl.substring(1);
  }
  return `${strippedUrl}`;
}

export const getAbsoluteUrl = (
  page: Page,
  pathOverride?: string,
  productSizeOverride?: string,
): string => {
  if (!page) {
    return "";
  }
  const lang = get(locale).split("-")[0];
  let path = stripBasePath(page.url.pathname);
  if (pathOverride) {
    path = pathOverride.startsWith("/") ? pathOverride : "/" + pathOverride;
  }
  const queryString =
    productSizeOverride &&
    queryToString({
      ...page.url.searchParams,
      size: productSizeOverride,
    });

  return (
    page.url.host +
    (cfg.BASE_PATH || "") +
    (lang === cfg.DEFAULT_LOCALE ? "" : `/${lang}`) +
    path.replace(`/${page.params.lang}`, "") +
    (queryString || "")
  );
};

export const getApiUrl = (page: Page, apiPath: string): string => {
  if (!page) {
    return "";
  }
  let path = page?.url?.pathname;
  if (apiPath) {
    path = apiPath.startsWith("/") ? apiPath : "/" + apiPath;
  }
  return "//" + page?.url?.host + (cfg.BASE_PATH || "") + "/api" + path;
};

export const getHostAndRegion = (page: Page): string => {
  const lang = get(locale).split("-")[0];
  return (
    page?.url?.host +
    (cfg.BASE_PATH ? cfg.BASE_PATH : "") +
    (lang === cfg.DEFAULT_LOCALE ? "" : `/${lang}`)
  );
};

export const isInternalLink = (href: string): boolean => {
  if (
    browser &&
    window &&
    href?.includes("//") &&
    !href?.includes("//" + window?.location.hostname)
  ) {
    return false;
  }
  return true;
};

export const linkTo = (link: string): string => {
  const lang = get(locale)?.split("-")[0];

  if (
    link &&
    (!isInternalLink(link) ||
      link?.startsWith("mailto:") ||
      link?.startsWith("tel:"))
  ) {
    return link;
  }

  if (link?.startsWith("./")) {
    link = link.split("./")[1];
  }

  if (!link?.startsWith("/")) {
    link = "/" + link;
  }

  if (link && link === "/") {
    link = "";
  }

  const useDefaultLang = cfg.DEFAULT_LOCALE && cfg.DEFAULT_LOCALE === lang;

  const fromStaticDocs = link.includes("docs/");

  return `${cfg.BASE_PATH || ""}${
    useDefaultLang || !lang || fromStaticDocs ? "" : "/" + lang
  }${link}`;
};

export const processInlineLinks = (input: string) => {
  // Find and replace href attributes in anchor tags
  return input?.replace(
    /<a\s+([^>]*)\s*href=([`"'])([^'"`]*)\2/gi,
    (match, attributes, quote, href) => {
      const updatedHref = href.includes("http") ? href : linkTo(href);
      if (attributes) {
        return `<a ${attributes} href=${quote}${updatedHref}${quote}`;
      } else {
        return `<a href=${quote}${updatedHref}${quote}`;
      }
    },
  );
};

export function normaliseURL(url: string): string {
  if (url && url.endsWith("/")) {
    return url.substring(0, url.length - 1);
  }
  return url;
}

export function buildURL(
  region: string,
  lang: string,
  page: Page,
  pathOverride?: string,
  defaultLang?: string,
): string {
  if (!page) {
    return "";
  }
  let path = stripBasePath(page.url.pathname);
  if (pathOverride) {
    path = pathOverride.startsWith("/") ? pathOverride : "/" + pathOverride;
  }
  const url = `https://www.belstaff.com/${region}${
    lang
      ? defaultLang
        ? defaultLang !== lang
          ? "/" + lang
          : ""
        : "/" + lang
      : ""
  }${path.replace(`/${page.params.lang}`, "")}`;
  return normaliseURL(url);
}

export function removeTrailingSlash(url: string): string {
  if (url && url.endsWith("/")) {
    return url.slice(0, -1);
  }
  return url;
}
